import { Inject, Injectable } from "@angular/core";
import { L10n } from "@skylab-angular-shared/core";
import { ValidationSource, ViewValidationReadonlyItem, ViewValidationViolationItem } from "@skylab-angular-shared/ui";
import { CollectionChangingAction, L10nBase, ModelBase, PropertyChangingJoinPoint, PropertyChangingJoinPointContext, ReadonlyRuleResult, RuleResult, ValidationResult, ViolationSeverity, WeaverBase } from "@skylab-shared/core";
import { isUndefined } from "@skylab-shared/utils";
import { BehaviorSubject, Subject, filter, takeUntil } from "rxjs";

type Readonly = ReadonlyRuleResult & RuleResult;
type Violation = ValidationResult<{sun: string}>;

type BaseValidationViolation = ViewValidationViolationItem & {changed: boolean};

type ValidationParseMode = 'add' | 'remove';

@Injectable({providedIn: 'root'})
export class ViewValidationService {
    public constructor(
        @Inject(L10n) private readonly l10n: L10nBase
    ) {}

    public create(
        sources: ValidationSource[],
        showAllValidations: BehaviorSubject<boolean> = new BehaviorSubject(true)
    ): ViewValidation {
        return new ViewValidation(sources, this.l10n, showAllValidations);
    }
}

export class ViewValidation {
    private changedProperties = new Map<string, boolean>();

    public violationsValidations: Map<string, BaseValidationViolation> = new Map();
    public violationValidation$ = new Subject<{key: string, validation: ViewValidationViolationItem}>();

    public readonlyValidations: Map<string, ViewValidationReadonlyItem> = new Map();
    public readonlyValidation$ = new Subject<{key: string, validation: ViewValidationReadonlyItem}>();

    private listeners: Function[] = [];
    private destroy$ = new Subject<void>();
    private destroyShowValidation$ = new Subject<void>();

    public constructor(
        private readonly sources: ValidationSource[],
        private readonly l10n: L10nBase,
        private readonly showAllValidations: BehaviorSubject<boolean> = new BehaviorSubject(true)
    ) {
        this.setInitialViolations();
        this.registerListeners();
    }

    public setInitialViolations(): void {
        const changed = this.showAllValidations.value;

        this.sources.forEach(source => {
            source.readonlyProperties?.forEach(elem => {
                const { obj, key } = this.getReadonlyObj(elem, 'add');
                this.readonlyValidations.set(key, obj);
            });
        });

        this.sources.forEach(source => {
            source.validationResult.forEach(elem => {
                const {obj, key} = this.getViolationObj(elem as Violation, 'add', changed);
                this.violationsValidations.set(key, {...obj});
            });
        });
    }

    private registerListeners(): void {
        this.listeners.push(WeaverBase.register(PropertyChangingJoinPoint, (ctx: PropertyChangingJoinPointContext) => {
            const key = this.getKey(ctx.propertyName, ctx.model.sun);
            if (this.changedProperties.has(key)) return;
            this.changedProperties.set(key, true);
        }));

        this.showAllValidations.pipe(
            takeUntil(this.destroy$),
            takeUntil(this.destroyShowValidation$),
            filter(Boolean))
        .subscribe(_ => {
            Array.from(this.violationsValidations).forEach(([key, value]) => {
                if (!value.changed) {
                    const validation = this.violationsValidations.get(key);
                    if (validation) {
                        validation.changed = true;
                        this.violationValidation$.next({key, validation});
                    }
                }
            });
            this.destroyShowValidation$.next();
        });

        this.sources.forEach(source => {
            if (source.readonlyProperties) {
                this.listeners.push(source.readonlyProperties.onCollectionChanged(data => {
                    if (data.action === CollectionChangingAction.Add) {
                        this.parseReadonly(data.newItems as Readonly[], 'add');
                    } else if ( data.action === CollectionChangingAction.Remove) {
                        this.parseReadonly(data.newItems as Readonly[], 'remove');
                    }
                }));
            }
        });
        this.sources.forEach(source => {
            this.listeners.push(source.validationResult.onCollectionChanged(data => {
                if (data.action === CollectionChangingAction.Add) {
                    this.parseViolation(data.newItems as Violation[], 'add');
                } else if ( data.action === CollectionChangingAction.Remove) {
                    this.parseViolation(data.oldItems as Violation[], 'remove');
                }
            }));
        });
    }

    private parseViolation(data: Violation[], mode: ValidationParseMode): void {
        data.forEach(elem => {
            const {obj, key} = this.getViolationObj(elem, mode);
            this.violationsValidations.set(key, obj);
            this.violationValidation$.next({key, validation: obj});
        });
    }

    private parseReadonly(data: Readonly[], mode: ValidationParseMode): void {
        data.forEach(elem => {
            const {obj, key} = this.getReadonlyObj(elem, mode);
            this.readonlyValidations.set(key, obj);
            this.readonlyValidation$.next({ key, validation: obj});
        });
    }

    private getReadonlyObj(elem: Readonly, mode: ValidationParseMode): {obj: ViewValidationReadonlyItem, key: string} {
        const key = this.getKey(elem.propertyName, elem.source.sun);
        const viol = this.readonlyValidations.get(key) || this.newViewValidationReadonly;
        const value = mode === 'add' ? elem.readonly : false;
        const obj = {...viol, readonly: value };
        return {obj, key};
    }

    private getViolationObj(elem: Violation, mode: ValidationParseMode, changed?: boolean): {obj: BaseValidationViolation, key: string} {
        const key = this.getKey(elem.propertyName, elem.source.sun);
        const viol = this.violationsValidations.get(key) || this.newViewValidationViolation;
        const parse = (key: keyof Pick<ViewValidationViolationItem, 'errors' | 'infos' | 'warnings'> ) => {
            if (mode === 'add') {
                viol[key].push(this.l10n._T(elem.ruleViolation.violation.message, elem.ruleViolation.violation.messageParams as any));
            } else {
                const idx = viol[key].findIndex(err => err === this.l10n._T(elem.ruleViolation.violation.message, elem.ruleViolation.violation.messageParams as any));
                if (idx > -1)  viol[key].splice(idx, 1);
            }
        };

        const toParse: Record<ViolationSeverity, keyof Pick<ViewValidationViolationItem, 'errors' | 'infos' | 'warnings'>> = {
            [ViolationSeverity.Error]: 'errors',
            [ViolationSeverity.Warning]: 'warnings',
            [ViolationSeverity.Info]: 'infos'
        };

        parse(toParse[elem.ruleViolation.violation.severity]);

        const obj = {...viol, changed: isUndefined(changed) ? this.showAllValidations.value || this.isPropertyChanged(elem, key) : changed };
        return { obj, key };
    }

    private isPropertyChanged(elem: Violation, key: string): boolean {
        if (elem.source instanceof ModelBase) {
            return this.changedProperties.has(key);
        }

        return true;
    }

    private get newViewValidationViolation(): BaseValidationViolation {
        return {errors: [], infos: [], warnings: [], changed: false};
    }

    private get newViewValidationReadonly(): ViewValidationReadonlyItem {
        return { readonly: false };
    }

    private getKey(propertyName: string, sun: string): string {
        return `${propertyName}.${sun}`;
    }

    public dispose(): void {
        this.listeners.forEach(dispose => dispose?.());
        this.destroy$.next();
    }
}
